import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, ThemeProvider, CssBaseline, CircularProgress } from '@mui/material';
import LoginPage from './pages/LoginPage';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import BottomNav from './components/BottomNav';
import ErrorBoundary from './components/ErrorBoundary';
import IncomingCallAlert from './components/IncomingCallAlert';
import DeductionAlert from './components/DeductionAlert';
import ChatMessageAlert from './components/ChatMessageAlert';
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeModeProvider, useThemeMode } from './ThemeModeContext';
import getTheme from './theme';
import { initPushNotifications } from './utils/pushNotifications';

// After a redeploy the previous build's hashed chunk files are gone, so a tab
// left open on the old version fails to load any page it hasn't opened yet.
// Reload once to pick up the new build instead of showing the error screen.
const lazyPage = (factory) =>
  React.lazy(() =>
    factory()
      .then((module) => {
        sessionStorage.removeItem('chunk-reload-attempted');
        return module;
      })
      .catch((err) => {
        if (!sessionStorage.getItem('chunk-reload-attempted')) {
          sessionStorage.setItem('chunk-reload-attempted', '1');
          window.location.reload();
          return new Promise(() => {});
        }
        throw err;
      })
  );

const DashboardPage = lazyPage(() => import('./pages/DashboardPage'));
const ClientsPage = lazyPage(() => import('./pages/ClientsPage'));
const OrdersPage = lazyPage(() => import('./pages/OrdersPage'));
const InquiriesPage = lazyPage(() => import('./pages/InquiriesPage'));
const DealsPage = lazyPage(() => import('./pages/DealsPage'));
const TasksPage = lazyPage(() => import('./pages/TasksPage'));
const ActivityLogPage = lazyPage(() => import('./pages/ActivityLogPage'));
const CustomerDetailsPage = lazyPage(() => import('./pages/CustomerDetailsPage'));
const UserPermissionsPage = lazyPage(() => import('./pages/UserPermissionsPage'));
const ProductsPage = lazyPage(() => import('./pages/ProductsPage'));
const ProductHistoryPage = lazyPage(() => import('./pages/ProductHistoryPage'));
const CandidatesPage = lazyPage(() => import('./pages/CandidatesPage'));
const CandidateDetailsPage = lazyPage(() => import('./pages/CandidateDetailsPage'));
const LeadsPage = lazyPage(() => import('./pages/LeadsPage'));
const LeadDetailsPage = lazyPage(() => import('./pages/LeadDetailsPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const DeliveryPage = lazyPage(() => import('./pages/DeliveryPage'));
const AccountsPage = lazyPage(() => import('./pages/AccountsPage'));
const CollectionsPage = lazyPage(() => import('./pages/CollectionsPage'));
const CollectionsReviewPage = lazyPage(() => import('./pages/CollectionsReviewPage'));
const ClientLedgerPage = lazyPage(() => import('./pages/ClientLedgerPage'));
const TreasuryPage = lazyPage(() => import('./pages/TreasuryPage'));
const ShippingPoliciesPage = lazyPage(() => import('./pages/ShippingPoliciesPage'));
const SuppliersPage = lazyPage(() => import('./pages/SuppliersPage'));
const ExpenseSettingsPage = lazyPage(() => import('./pages/ExpenseSettingsPage'));
const CompanyExpensesPage = lazyPage(() => import('./pages/CompanyExpensesPage'));
const ExpenseReportPage = lazyPage(() => import('./pages/ExpenseReportPage'));
const CommissionsPage = lazyPage(() => import('./pages/CommissionsPage'));
const RepCommissionsPage = lazyPage(() => import('./pages/RepCommissionsPage'));
const PayrollPage = lazyPage(() => import('./pages/PayrollPage'));
const TeamDeductionsPage = lazyPage(() => import('./pages/TeamDeductionsPage'));
const ReportsCenterPage = lazyPage(() => import('./pages/ReportsCenterPage'));
const DuplicatesPage = lazyPage(() => import('./pages/DuplicatesPage'));
const SystemLogsPage = lazyPage(() => import('./pages/SystemLogsPage'));
const ReturnsPage = lazyPage(() => import('./pages/ReturnsPage'));
const VisitsPage = lazyPage(() => import('./pages/VisitsPage'));
const FollowupsPage = lazyPage(() => import('./pages/FollowupsPage'));
const AttendancePage = lazyPage(() => import('./pages/AttendancePage'));
const AttendanceDashboardPage = lazyPage(() => import('./pages/AttendanceDashboardPage'));
const InventoryAuditPage = lazyPage(() => import('./pages/InventoryAuditPage'));
const ApprovedAdjustmentsPage = lazyPage(() => import('./pages/ApprovedAdjustmentsPage'));
const WarehouseDeliveryPage = lazyPage(() => import('./pages/WarehouseDeliveryPage'));
const CallsPage = lazyPage(() => import('./pages/CallsPage'));
const ChatPage = lazyPage(() => import('./pages/ChatPage'));
const JobApplicationPage = lazyPage(() => import('./pages/JobApplicationPage'));
const PublicInvoicePage = lazyPage(() => import('./pages/PublicInvoicePage'));
const OrderChangesPage = lazyPage(() => import('./pages/OrderChangesPage'));


const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

const ALL_RESTRICTED_ROLES = ['hr', 'developer', 'marketing', 'warehouse', 'delivery_rep', 'administrative'];

const HOME_ROUTE_BY_ROLE = {
  hr: '/candidates',
  developer: '/tasks',
  marketing: '/tasks',
  warehouse: '/deals',
  delivery_rep: '/delivery',
  administrative: '/tasks',
};

const ProtectedRoute = ({ children, allowRoles }) => {
  const { currentUser } = useAuth();
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  const home = HOME_ROUTE_BY_ROLE[currentUser.roleSlug];
  if (home && !(allowRoles || []).includes(currentUser.roleSlug)) {
    return <Navigate to={home} replace />;
  }
  return children;
};

function AppRoutes() {
  const { currentUser } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    initPushNotifications((link) => navigate(link));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public job-application form - no login, no CRM chrome (Navbar/Sidebar/
  // BottomNav), reachable by anyone with the link.
  if (location.pathname === '/apply') {
    return (
      <React.Suspense fallback={<PageLoader />}>
        <JobApplicationPage />
      </React.Suspense>
    );
  }

  // Public shareable invoice - same idea, linked from the WhatsApp
  // "shipped" message instead of trying to attach an image (wa.me's
  // click-to-chat only supports pre-filled text, no media).
  if (location.pathname.startsWith('/invoice/')) {
    const orderId = location.pathname.slice('/invoice/'.length);
    return (
      <React.Suspense fallback={<PageLoader />}>
        <PublicInvoicePage orderId={orderId} />
      </React.Suspense>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      {currentUser && (
        <Box sx={{ width: { xs: 0, md: 240 }, flexShrink: 0 }}>
          <Sidebar mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        </Box>
      )}
      <UpdateAvailableBanner />
      {currentUser && <IncomingCallAlert />}
      {currentUser && <DeductionAlert />}
      {currentUser && <ChatMessageAlert />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 1.5, sm: 3 },
          pb: { xs: currentUser ? 9 : 1.5, sm: 3 },
          overflow: 'auto',
        }}
      >
        <Navbar />
        <ErrorBoundary key={location.pathname}>
        <React.Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:customerId"
            element={
              <ProtectedRoute>
                <CustomerDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <OrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <InquiriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals"
            element={
              <ProtectedRoute allowRoles={['warehouse', 'administrative']}>
                <DealsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <ProtectedRoute allowRoles={['developer', 'marketing', 'administrative', 'delivery_rep']}>
                <TasksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/activities"
            element={
              <ProtectedRoute>
                <ActivityLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute>
                <UserPermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <ProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id/history"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <ProductHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidates"
            element={
              <ProtectedRoute allowRoles={['hr', 'sales_rep', 'administrative']}>
                <CandidatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidates/:candidateId"
            element={
              <ProtectedRoute allowRoles={['hr', 'sales_rep', 'administrative']}>
                <CandidateDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute allowRoles={ALL_RESTRICTED_ROLES}>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/delivery"
            element={
              <ProtectedRoute allowRoles={['delivery_rep', 'outside_sales', 'admin', 'sales_manager', 'accounts_manager', 'administrative']}>
                <DeliveryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <AccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/collections"
            element={
              <ProtectedRoute>
                <CollectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/collections-review"
            element={
              <ProtectedRoute>
                <CollectionsReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/client-ledger"
            element={
              <ProtectedRoute>
                <ClientLedgerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/treasury"
            element={
              <ProtectedRoute>
                <TreasuryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/shipping-policies"
            element={
              <ProtectedRoute>
                <ShippingPoliciesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/suppliers"
            element={
              <ProtectedRoute>
                <SuppliersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/expense-settings"
            element={
              <ProtectedRoute>
                <ExpenseSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/company-expenses"
            element={
              <ProtectedRoute>
                <CompanyExpensesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/expense-report"
            element={
              <ProtectedRoute>
                <ExpenseReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/commissions"
            element={
              <ProtectedRoute>
                <CommissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/rep-commissions"
            element={
              <ProtectedRoute>
                <RepCommissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/payroll"
            element={
              <ProtectedRoute>
                <PayrollPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounts/team-deductions"
            element={
              <ProtectedRoute>
                <TeamDeductionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsCenterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-changes"
            element={
              <ProtectedRoute>
                <OrderChangesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/duplicates"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <DuplicatesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-logs"
            element={
              <ProtectedRoute>
                <SystemLogsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/returns"
            element={
              <ProtectedRoute allowRoles={['warehouse', 'accounts_manager', 'sales_manager']}>
                <ReturnsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/visits"
            element={
              <ProtectedRoute allowRoles={['outside_sales', 'admin', 'sales_manager', 'accounts_manager', 'administrative']}>
                <VisitsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/followups"
            element={
              <ProtectedRoute allowRoles={['sales_rep', 'outside_sales', 'admin', 'sales_manager', 'administrative']}>
                <FollowupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute allowRoles={ALL_RESTRICTED_ROLES}>
                <AttendancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance-dashboard"
            element={
              <ProtectedRoute allowRoles={['administrative']}>
                <AttendanceDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-audit"
            element={
              <ProtectedRoute allowRoles={['warehouse']}>
                <InventoryAuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory-approvals"
            element={
              <ProtectedRoute>
                <ApprovedAdjustmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/warehouse-delivery"
            element={
              <ProtectedRoute allowRoles={['warehouse']}>
                <WarehouseDeliveryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calls"
            element={
              <ProtectedRoute>
                <CallsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute allowRoles={ALL_RESTRICTED_ROLES}>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads"
            element={
              <ProtectedRoute allowRoles={ALL_RESTRICTED_ROLES}>
                <LeadsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leads/:leadId"
            element={
              <ProtectedRoute allowRoles={ALL_RESTRICTED_ROLES}>
                <LeadDetailsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
        </React.Suspense>
        </ErrorBoundary>
      </Box>
      {currentUser && <BottomNav onMoreClick={() => setMobileNavOpen(true)} />}
    </div>
  );
}

function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = React.useMemo(() => getTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;
